// Tiny typed API client. Talks to the date-versioned object engine.
// Cloudflare Access cookies ride along automatically (same-origin /api proxy
// in dev, or same registrable domain in prod).

const API_BASE =
  (import.meta as any).env?.VITE_API_BASE ??
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? '/api'
    : 'https://thehub-api.hphcorp.workers.dev');

export interface ObjectRecord {
  id: string;
  object_type: string;
  owner_id: string | null;
  properties: Record<string, unknown>;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface ListResponse {
  results: ObjectRecord[];
  paging?: { next?: { after: string } };
}

export interface HistoryRow {
  id: string;
  property: string;
  old_value: unknown;
  new_value: unknown;
  changed_by_user: string | null;
  source: string;
  changed_at: number;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  // In dev with no Access fronting, we send a dev token. In prod, Access has
  // already authenticated and the cookie is included via credentials: include.
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  // Dev convenience: if running on localhost, ship the dev token.
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    !headers.has('Authorization')
  ) {
    headers.set(
      'Authorization',
      'Bearer dev:01WORKSPACE0000000000000DEF:01USER000000000000000ADMIN',
    );
  }

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...init,
    credentials: 'include',
    headers,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new ApiError(res.status, res.statusText, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly statusText: string,
    readonly body: string,
  ) {
    super(`${status} ${statusText}: ${body}`);
  }
}

export const api = {
  health: () => request<{ status: string; checks: Record<string, unknown> }>('/health'),
  me: () => request<{ workspace_id: string; actor: unknown }>('/2026-05/workspaces/me'),

  list: (type: string, opts?: { limit?: number; after?: string }) => {
    const qs = new URLSearchParams();
    if (opts?.limit) qs.set('limit', String(opts.limit));
    if (opts?.after) qs.set('after', opts.after);
    const q = qs.toString();
    return request<ListResponse>(
      `/2026-05/objects/${type}${q ? '?' + q : ''}`,
    );
  },

  read: (type: string, id: string) =>
    request<ObjectRecord>(`/2026-05/objects/${type}/${id}`),

  create: (type: string, properties: Record<string, unknown>) =>
    request<ObjectRecord>(`/2026-05/objects/${type}`, {
      method: 'POST',
      body: JSON.stringify({ properties, source: { via: 'ui' } }),
    }),

  update: (type: string, id: string, properties: Record<string, unknown>) =>
    request<ObjectRecord>(`/2026-05/objects/${type}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ properties, source: { via: 'ui' } }),
    }),

  archive: (type: string, id: string) =>
    request<void>(`/2026-05/objects/${type}/${id}`, { method: 'DELETE' }),

  restore: (type: string, id: string) =>
    request<void>(`/2026-05/objects/${type}/${id}/restore`, { method: 'POST' }),

  history: (type: string, id: string, limit = 50) =>
    request<{ results: HistoryRow[] }>(
      `/2026-05/objects/${type}/${id}/history?limit=${limit}`,
    ),
};
